/**
 * `/`: the site's front page. The site is about reading orders in fantasy
 * worlds, and this is the threshold: what the site is, then the worlds.
 *
 * One card per world with something to read, its counts from the database.
 * No placeholder cards for worlds that do not exist, and no names of worlds
 * that cannot be delivered: a greyed-out card is a promise. The closing panel
 * asks instead which world should come next, and leads to the contact form.
 *
 * On ink, the whole page: it is the threshold, not a reading surface.
 */

import { Link } from 'react-router'
import { worldPath } from '../../shared/routes.ts'
import { usePageTitle } from '../components/Catalogue.tsx'
import { Compass, Constellation } from '../components/Ornament.tsx'
import { useI18n } from '../i18n/index.tsx'
import { fill, pick, useWorlds, worldName } from '../lib/catalogue.ts'

export function Worlds() {
  const { t, locale } = useI18n()
  const worlds = useWorlds()
  usePageTitle(null)

  return (
    <>
      <section className="threshold__hero">
        <Constellation />
        <Compass size={140} on="ink" />
        <h1>{t('app.name')}</h1>
        <p className="threshold__lead">{t('worlds.lead')}</p>
      </section>

      {worlds.status === 'loading' ? (
        <section className="panel">
          <p>{t('app.loading')}</p>
        </section>
      ) : worlds.status === 'error' ? (
        <section className="panel">
          <p className="alert alert--error">{t('catalogue.error')}</p>
        </section>
      ) : worlds.worlds.length === 0 ? (
        <section className="panel">
          <h2>{t('catalogue.not_loaded.title')}</h2>
          <p>{t('catalogue.not_loaded.body')}</p>
        </section>
      ) : (
        <div>
          {worlds.worlds.map((world) => {
            const tagline = pick(world, 'tagline', locale)
            const description = pick(world, 'description', locale)
            return (
              <article key={world.slug} className="world-card">
                <h2>
                  <Link to={worldPath(world.slug)}>{worldName(world, locale)}</Link>
                </h2>
                {tagline === null ? null : <p className="world-card__tagline">{tagline}</p>}
                {description === null ? null : <p>{description}</p>}
                <p className="label">{fill(t('worlds.counts'), { works: world.works, paths: world.paths })}</p>
              </article>
            )
          })}
        </div>
      )}

      <section className="next-world">
        <h2>
          <Link to="/contact">{t('worlds.next.title')}</Link>
        </h2>
        <p>{t('worlds.next.body')}</p>
      </section>
    </>
  )
}
